import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Grid3x3, List, Search, PlusCircle, Trash2, BarChart3, Clock,
  FolderPlus, ChevronRight, ArrowLeft, Pencil, X, Plus,
  FolderOutput, GripVertical
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FolderCard({ folder, onOpen, onRename, onDelete, canEdit, isDragOver, onDragOver, onDragLeave, onDrop }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  const handleRename = (e) => {
    e.preventDefault();
    if (name.trim() && name.trim() !== folder.name) onRename(folder.folder_id, name.trim());
    setEditing(false);
  };
  return (
    <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      onClick={() => !editing && onOpen(folder.folder_id)}
      className={`cursor-pointer group relative transition-all duration-200 ${isDragOver ? "scale-105" : "hover:-translate-y-0.5 hover:shadow-md"}`}>
      <div className={`relative ${isDragOver ? "ring-2 ring-orange-400 ring-offset-2 rounded-2xl" : ""}`}>
        <div className="absolute -top-[10px] left-3 w-20 h-[14px] rounded-t-lg bg-gradient-to-r from-amber-400 to-orange-400" />
        <div className={`bg-gradient-to-br from-amber-50 to-orange-50 border-2 rounded-2xl p-5 pt-4 min-h-[140px] flex flex-col justify-between ${isDragOver ? "border-orange-400 bg-orange-100/50" : "border-amber-200/80"}`}>
          <div>
            {editing ? (
              <form onSubmit={handleRename} onClick={e => e.stopPropagation()} className="flex items-center gap-1">
                <input autoFocus value={name} onChange={e => setName(e.target.value)} className="text-sm font-bold text-slate-800 bg-white border border-orange-300 rounded-lg px-2 py-1 outline-none w-full" />
                <button type="submit" className="p-1 text-emerald-500"><PlusCircle size={14} /></button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(false); setName(folder.name); }} className="p-1 text-slate-400"><X size={14} /></button>
              </form>
            ) : (
              <h3 className="text-sm font-bold text-slate-800 truncate">{folder.name}</h3>
            )}
            <p className="text-xs text-amber-600/70 mt-1">{folder.analysis_count} {folder.analysis_count === 1 ? "analysis" : "analyses"}</p>
          </div>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-1 text-xs text-amber-500"><span>Open</span> <ChevronRight size={12} /></div>
            {canEdit && !editing && (
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} className="p-1 rounded bg-white/80 text-slate-400 hover:text-blue-500 shadow-sm"><Pencil size={10} /></button>
                <button onClick={(e) => { e.stopPropagation(); onDelete(folder.folder_id, e); }} className="p-1 rounded bg-white/80 text-slate-400 hover:text-red-500 shadow-sm"><Trash2 size={10} /></button>
              </div>
            )}
          </div>
        </div>
      </div>
      {isDragOver && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-xs font-semibold text-orange-600 bg-white/90 px-3 py-1 rounded-full shadow">Drop here</span>
        </div>
      )}
    </div>
  );
}

function AnalysisCard({ a, navigate, canEdit, onDelete, compareMode, selectedIds, toggleSelect, onRemoveFromFolder, insideFolder }) {
  const scoreColor = (s) => s >= 80 ? "text-emerald-600 bg-emerald-50" : s >= 50 ? "text-amber-600 bg-amber-50" : "text-red-500 bg-red-50";
  const statusBadge = (s) => ({ completed: "bg-emerald-50 text-emerald-700", processing: "bg-amber-50 text-amber-700", failed: "bg-red-50 text-red-700" }[s] || "");
  const handleClick = () => {
    if (compareMode && a.status === "completed") toggleSelect(a.id, { stopPropagation: () => {} });
    else if (a.status === "completed") navigate(`/analyze/${a.id}`);
  };
  return (
    <div draggable={canEdit && !compareMode && !insideFolder}
      onDragStart={(e) => { e.dataTransfer.setData("analysis_id", a.id); e.dataTransfer.effectAllowed = "move"; }}
      onClick={handleClick}
      className={`bg-white rounded-2xl border-2 p-4 transition-all relative group ${compareMode && selectedIds.includes(a.id) ? "border-indigo-500 shadow-md shadow-indigo-50" : "border-slate-200"} ${a.status === "completed" ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5" : ""} ${canEdit && !compareMode && !insideFolder ? "cursor-grab active:cursor-grabbing" : ""}`}>
      {canEdit && !compareMode && !insideFolder && (
        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-30 transition-opacity"><GripVertical size={12} className="text-slate-400" /></div>
      )}
      {compareMode && a.status === "completed" && (
        <div className={`absolute top-3 right-3 w-5 h-5 rounded-md border-2 flex items-center justify-center ${selectedIds.includes(a.id) ? "bg-indigo-500 border-indigo-500" : "border-slate-300 bg-white"}`}>
          {selectedIds.includes(a.id) && <span className="text-white text-[10px] font-bold">✓</span>}
        </div>
      )}
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center"><BarChart3 size={16} className="text-orange-500" /></div>
        {a.status === "completed" && a.results && (
          <div className={`px-2 py-1 rounded-lg ${scoreColor(a.results.neuro_score)}`}>
            <span className="text-lg font-bold" style={{ fontFamily: "JetBrains Mono" }}>{a.results.neuro_score}</span>
          </div>
        )}
        {a.status !== "completed" && <Badge className={`text-[10px] ${statusBadge(a.status)} border-0`}>{a.status}</Badge>}
      </div>
      <h3 className="text-sm font-bold text-slate-800 mb-1 truncate">{a.ad_name}</h3>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="secondary" className="text-[9px]">{a.category}</Badge>
        <Badge variant="secondary" className="text-[9px]">{a.platform}</Badge>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-[10px] text-slate-400"><Clock size={10} />{new Date(a.created_at).toLocaleDateString()}</div>
        <div className="flex items-center gap-1">
          {insideFolder && canEdit && (
            <button onClick={(e) => { e.stopPropagation(); onRemoveFromFolder(a.id); }} title="Remove from folder"
              className="p-1 rounded text-slate-300 hover:text-amber-500 transition-colors opacity-0 group-hover:opacity-100"><FolderOutput size={12} /></button>
          )}
          {canEdit && <button onClick={(e) => onDelete(a.id, e)} className="p-1 rounded text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={12} /></button>}
        </div>
      </div>
    </div>
  );
}

function AddToFolderModal({ allAnalyses, folders, currentFolderId, onAdd, onClose }) {
  const [search, setSearch] = useState("");
  const available = allAnalyses.filter(a => a.status === "completed");
  const filtered = available.filter(a => !search || a.ad_name.toLowerCase().includes(search.toLowerCase()) || a.category?.toLowerCase().includes(search.toLowerCase()));
  const getFolderName = (fid) => { if (!fid || fid === currentFolderId) return null; const f = folders.find(f => f.folder_id === fid); return f ? f.name : null; };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[70vh] flex flex-col shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">Add analyses to folder</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={16} /></button>
        </div>
        <div className="px-6 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
            <Search size={14} className="text-slate-400" />
            <input type="text" placeholder="Search analyses..." value={search} onChange={e => setSearch(e.target.value)} className="text-sm outline-none flex-1 bg-transparent placeholder:text-slate-400" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-1">
          {filtered.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No analyses found</p>}
          {filtered.map(a => {
            const isHere = a.folder_id === currentFolderId;
            const otherFolder = getFolderName(a.folder_id);
            return (
              <div key={a.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${isHere ? "bg-emerald-50/50 opacity-60" : "hover:bg-slate-50 cursor-pointer"}`}
                onClick={() => !isHere && onAdd(a.id)}>
                <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0"><BarChart3 size={12} className="text-orange-500" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{a.ad_name}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400">{a.category} · {a.platform}</span>
                    {a.results?.neuro_score && <span className={`text-[10px] font-bold ${a.results.neuro_score >= 80 ? "text-emerald-600" : a.results.neuro_score >= 50 ? "text-amber-600" : "text-red-500"}`}>{a.results.neuro_score}</span>}
                  </div>
                </div>
                {isHere ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-600 font-medium whitespace-nowrap">Already here</span>
                ) : otherFolder ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-medium whitespace-nowrap truncate max-w-[100px]" title={`In: ${otherFolder}`}>In: {otherFolder}</span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 whitespace-nowrap">No folder</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function MyAnalysesPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [analyses, setAnalyses] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [sortBy, setSortBy] = useState("date-desc");
  const [compareMode, setCompareMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();
  const canEdit = !user?.team_role || ["owner", "admin", "editor"].includes(user?.team_role);

  const fetchData = useCallback(async () => {
    try {
      const [aRes, fRes] = await Promise.all([
        axios.get(`${API}/analyses`, { withCredentials: true }),
        axios.get(`${API}/folders`, { withCredentials: true })
      ]);
      setAnalyses(aRes.data);
      setFolders(fRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this analysis?")) return;
    try {
      await axios.delete(`${API}/analyses/${id}`, { withCredentials: true });
      setAnalyses(prev => prev.filter(a => a.id !== id));
      setSelectedIds(prev => prev.filter(sid => sid !== id));
    } catch {}
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : prev.length < 4 ? [...prev, id] : prev);
  };

  const handleCompare = () => { if (selectedIds.length >= 2) navigate(`/compare?ids=${selectedIds.join(",")}`); };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    try { await axios.post(`${API}/folders`, { name: newFolderName.trim() }, { withCredentials: true }); setNewFolderName(""); setShowNewFolder(false); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleRenameFolder = async (fid, name) => {
    try { await axios.put(`${API}/folders/${fid}`, { name }, { withCredentials: true }); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteFolder = async (fid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this folder? Analyses inside will return to the main view.")) return;
    try { await axios.delete(`${API}/folders/${fid}`, { withCredentials: true }); if (activeFolder === fid) setActiveFolder(null); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const moveToFolder = async (analysisId, folderId) => {
    try {
      await axios.put(`${API}/analyses/${analysisId}/folder`, { folder_id: folderId }, { withCredentials: true });
      setAnalyses(prev => prev.map(a => a.id === analysisId ? { ...a, folder_id: folderId } : a));
      fetchData(); // refresh folder counts
    } catch {}
  };

  const removeFromFolder = async (analysisId) => { await moveToFolder(analysisId, null); };

  const handleFolderDragOver = (fid, e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverFolder(fid); };
  const handleFolderDragLeave = () => { setDragOverFolder(null); };
  const handleFolderDrop = (fid, e) => { e.preventDefault(); const aid = e.dataTransfer.getData("analysis_id"); if (aid) moveToFolder(aid, fid); setDragOverFolder(null); };

  const visibleAnalyses = activeFolder ? analyses.filter(a => a.folder_id === activeFolder) : analyses.filter(a => !a.folder_id);
  const filtered = visibleAnalyses
    .filter(a => !search || a.ad_name.toLowerCase().includes(search.toLowerCase()) || a.category?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "date-desc") return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === "date-asc") return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === "score-desc") return (b.results?.neuro_score || 0) - (a.results?.neuro_score || 0);
      if (sortBy === "score-asc") return (a.results?.neuro_score || 0) - (b.results?.neuro_score || 0);
      return 0;
    });

  const activeFolderData = folders.find(f => f.folder_id === activeFolder);

  // Sort folders: numeric first, then alphabetical
  const sortedFolders = [...folders].sort((a, b) => {
    const numA = parseInt(a.name.match(/^\d+/)?.[0]);
    const numB = parseInt(b.name.match(/^\d+/)?.[0]);
    const hasNumA = !isNaN(numA);
    const hasNumB = !isNaN(numB);
    if (hasNumA && hasNumB) return numA - numB || a.name.localeCompare(b.name);
    if (hasNumA) return -1;
    if (hasNumB) return 1;
    return a.name.localeCompare(b.name);
  });

  if (loading) return (
    <div className="space-y-5 max-w-6xl">
      <div className="h-8 w-48 bg-slate-200 rounded animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i => <div key={i} className="h-48 bg-white rounded-2xl animate-pulse" />)}</div>
    </div>
  );

  return (
    <div data-testid="my-analyses-page" className="max-w-6xl space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {activeFolder && <button onClick={() => setActiveFolder(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"><ArrowLeft size={18} /></button>}
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>{activeFolder ? activeFolderData?.name || "Folder" : "My Analyses"}</h1>
          {activeFolder && <Badge variant="secondary" className="text-[10px]">{filtered.length} analyses</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {compareMode ? (
            <>
              <span className="text-xs text-slate-500">{selectedIds.length}/4 selected</span>
              <Button variant="outline" size="sm" className="rounded-xl text-xs" onClick={() => { setCompareMode(false); setSelectedIds([]); }}>Cancel</Button>
              <Button size="sm" className="rounded-xl text-xs bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleCompare} disabled={selectedIds.length < 2}>Compare ({selectedIds.length})</Button>
            </>
          ) : (
            <>
              {activeFolder && canEdit && <Button variant="outline" size="sm" className="rounded-xl text-xs" onClick={() => setShowAddModal(true)}><Plus size={14} className="mr-1" /> Add analyses</Button>}
              <Button variant="outline" size="sm" className="rounded-xl text-xs" onClick={() => setCompareMode(true)}><BarChart3 size={14} className="mr-1" /> Compare</Button>
              {canEdit && <Button data-testid="new-analysis-btn" onClick={() => navigate("/analyze/new")} className="gradient-btn rounded-xl text-sm font-semibold"><span className="flex items-center gap-2"><PlusCircle size={16} /> New Analysis</span></Button>}
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 px-3 py-2 flex-1 max-w-sm">
          <Search size={14} className="text-slate-400" />
          <input type="text" placeholder="Search by name or category..." value={search} onChange={(e) => setSearch(e.target.value)} className="text-sm outline-none flex-1 bg-transparent placeholder:text-slate-400" />
        </div>
        {!activeFolder && canEdit && (
          showNewFolder ? (
            <form onSubmit={handleCreateFolder} className="flex items-center gap-1">
              <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Folder name..."
                className="h-9 px-3 text-sm border border-orange-300 rounded-xl outline-none w-36" />
              <button type="submit" className="h-9 px-3 text-xs bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-medium">Create</button>
              <button type="button" onClick={() => { setShowNewFolder(false); setNewFolderName(""); }}
                className="h-9 px-2 text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </form>
          ) : (
            <button onClick={() => setShowNewFolder(true)}
              className="h-9 px-3 flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-600 hover:border-orange-300 hover:text-orange-600 transition-colors">
              <FolderPlus size={14} /> New Folder
            </button>
          )
        )}
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-600 outline-none">
          <option value="date-desc">Newest first</option><option value="date-asc">Oldest first</option>
          <option value="score-desc">Highest score</option><option value="score-asc">Lowest score</option>
        </select>
        <div className="flex bg-white rounded-xl border border-slate-200 p-0.5">
          <button onClick={() => setViewMode("grid")} className={`p-1.5 rounded-lg ${viewMode === "grid" ? "bg-orange-50 text-orange-600" : "text-slate-400"}`}><Grid3x3 size={16} /></button>
          <button onClick={() => setViewMode("list")} className={`p-1.5 rounded-lg ${viewMode === "list" ? "bg-orange-50 text-orange-600" : "text-slate-400"}`}><List size={16} /></button>
        </div>
      </div>

      {!activeFolder && folders.length > 0 && filtered.length > 0 && canEdit && viewMode === "grid" && (
        <p className="text-[11px] text-slate-400 flex items-center gap-1"><GripVertical size={10} /> Drag an analysis onto a folder to organize it</p>
      )}

      {/* Grid view */}
      {viewMode === "grid" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {!activeFolder && sortedFolders.map(f => (
            <FolderCard key={f.folder_id} folder={f} onOpen={setActiveFolder} onRename={handleRenameFolder} onDelete={handleDeleteFolder} canEdit={canEdit}
              isDragOver={dragOverFolder === f.folder_id} onDragOver={(e) => handleFolderDragOver(f.folder_id, e)} onDragLeave={handleFolderDragLeave} onDrop={(e) => handleFolderDrop(f.folder_id, e)} />
          ))}
          {filtered.map(a => (
            <AnalysisCard key={a.id} a={a} navigate={navigate} canEdit={canEdit} onDelete={handleDelete} compareMode={compareMode}
              selectedIds={selectedIds} toggleSelect={toggleSelect} onRemoveFromFolder={removeFromFolder} insideFolder={!!activeFolder} />
          ))}
        </div>
      )}

      {/* List view */}
      {viewMode === "list" && (
        <>
          {!activeFolder && sortedFolders.length > 0 && (
            <div className="space-y-2 mb-4">
              {sortedFolders.map(f => (
                <div key={f.folder_id} onDragOver={(e) => handleFolderDragOver(f.folder_id, e)} onDragLeave={handleFolderDragLeave} onDrop={(e) => handleFolderDrop(f.folder_id, e)}
                  onClick={() => setActiveFolder(f.folder_id)}
                  className={`flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border rounded-xl cursor-pointer hover:shadow-sm transition-all ${dragOverFolder === f.folder_id ? "border-orange-400 ring-2 ring-orange-200" : "border-amber-200/80"}`}>
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center"><span className="text-amber-600 text-xs font-bold">{f.analysis_count}</span></div>
                  <span className="text-sm font-medium text-slate-800 flex-1">{f.name}</span>
                  <ChevronRight size={14} className="text-amber-400" />
                </div>
              ))}
            </div>
          )}
          {filtered.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-slate-100">
                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Ad Name</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3 hidden md:table-cell">Category</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3 hidden md:table-cell">Platform</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Score</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3 hidden md:table-cell">Date</th>
                  {canEdit && <th className="w-16" />}
                </tr></thead>
                <tbody>{filtered.map(a => (
                  <tr key={a.id} draggable={canEdit && !activeFolder} onDragStart={(e) => { e.dataTransfer.setData("analysis_id", a.id); }}
                    onClick={() => a.status === "completed" ? navigate(`/analyze/${a.id}`) : null}
                    className={`border-b border-slate-50 ${a.status === "completed" ? "cursor-pointer hover:bg-slate-50" : ""}`}>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">{a.ad_name}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 hidden md:table-cell">{a.category}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 hidden md:table-cell">{a.platform}</td>
                    <td className="px-4 py-3">{a.status === "completed" && a.results ? (
                      <span className={`text-base font-bold ${a.results.neuro_score >= 80 ? "text-emerald-600" : a.results.neuro_score >= 50 ? "text-amber-600" : "text-red-500"}`} style={{ fontFamily: "JetBrains Mono" }}>{a.results.neuro_score}</span>
                    ) : <span className="text-xs text-slate-400">—</span>}</td>
                    <td className="px-4 py-3"><Badge className={`text-[10px] ${({ completed: "bg-emerald-50 text-emerald-700", processing: "bg-amber-50 text-amber-700", failed: "bg-red-50 text-red-700" }[a.status] || "")} border-0`}>{a.status}</Badge></td>
                    <td className="px-4 py-3 text-xs text-slate-400 hidden md:table-cell">{new Date(a.created_at).toLocaleDateString()}</td>
                    {canEdit && (
                      <td className="px-2 py-3"><div className="flex items-center gap-1">
                        {activeFolder && <button onClick={(e) => { e.stopPropagation(); removeFromFolder(a.id); }} className="p-1 text-slate-300 hover:text-amber-500"><FolderOutput size={12} /></button>}
                        <button onClick={(e) => handleDelete(a.id, e)} className="p-1 text-slate-300 hover:text-red-500"><Trash2 size={12} /></button>
                      </div></td>
                    )}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {filtered.length === 0 && (!activeFolder ? folders.length === 0 : true) && (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4"><BarChart3 size={24} className="text-slate-400" /></div>
          <p className="text-sm font-medium text-slate-700 mb-1">{search ? "No matching analyses" : activeFolder ? "This folder is empty" : "No analyses yet"}</p>
          <p className="text-xs text-slate-500 mb-4">{activeFolder ? "Click '+ Add analyses' to add existing analyses here" : "Upload your first ad to get started"}</p>
          {activeFolder && canEdit ? (
            <Button variant="outline" onClick={() => setShowAddModal(true)} className="rounded-xl text-sm"><Plus size={14} className="mr-1" /> Add analyses</Button>
          ) : canEdit && !activeFolder ? (
            <Button onClick={() => navigate("/analyze/new")} className="gradient-btn rounded-xl text-sm font-semibold">Start First Analysis</Button>
          ) : null}
        </div>
      )}

      {showAddModal && <AddToFolderModal allAnalyses={analyses} folders={folders} currentFolderId={activeFolder} onAdd={(id) => moveToFolder(id, activeFolder)} onClose={() => setShowAddModal(false)} />}
    </div>
  );
}
